import { hasOwnProperty } from "collection-utils";
import { PossibleBarcode } from "../chunk-clustering";
import { DecodeParameters, DecodeResult } from "../decoder";
import { AngleCluster } from "../locator/angle-clustering";
import { ColorSpace } from "../locator/color-spaces";

export const fullChunkRequestMessageName = "process-full-chunks";
export const preThresholdRequestMessageName = "process-pre-threshold-chunks";
export const chunkAngleResponseMessageName = "chunk-angle-response";

export const decodeBarcodeRequestMessageName = "process-decode-barcode";
export const barcodeResponseMessageName = "barcode-response";

export interface MessagePacketCommon {
    srcClientID: number;
    dstClientID: number;
}

export function isMessagePacketCommon(m: unknown): m is MessagePacketCommon {
    return (
        hasOwnProperty(m, "srcClientID") &&
        hasOwnProperty(m, "dstClientID") &&
        typeof m.srcClientID === "number" &&
        typeof m.dstClientID === "number"
    );
}

export interface UnifiedLocationRequestMessage extends MessagePacketCommon {
    buffers: ArrayBuffer;
    imgWidth: number;
    imgHeight: number;
    chunkDim: number;
    chunkScheduleOffset: number;
    chunksToProcess: number;
    colorSpace: ColorSpace;
    angleDelta: number;
}

export interface FullChunkRequestMessage extends UnifiedLocationRequestMessage {
    kind: typeof fullChunkRequestMessageName;
}

export interface PreThresholdRequestMessage extends UnifiedLocationRequestMessage {
    kind: typeof preThresholdRequestMessageName;
}

export function isUnifiedLocationRequestMessage(m: unknown): m is UnifiedLocationRequestMessage {
    if (!hasOwnProperty(m, "kind")) return false;

    return m.kind === fullChunkRequestMessageName || m.kind === preThresholdRequestMessageName;
}

export function isFullChunkRequestMessage(m: unknown): m is FullChunkRequestMessage {
    if (!hasOwnProperty(m, "kind")) return false;

    return m.kind === fullChunkRequestMessageName;
}

export function isPreThresholdRequestMessage(m: unknown): m is PreThresholdRequestMessage {
    if (!hasOwnProperty(m, "kind")) return false;

    return m.kind === preThresholdRequestMessageName;
}

export interface DecodeBarcodeRequestMessage extends MessagePacketCommon {
    kind: typeof decodeBarcodeRequestMessageName;
    imgWidth: number;
    imgBuffer: ArrayBuffer;
    colorSpaces: ColorSpace[];
    // We don't use decodeStep in this message; it's used in the
    // calling thread.
    decodeParams: DecodeParameters;
}

export function isDecodeBarcodeRequestMessage(m: unknown): m is DecodeBarcodeRequestMessage {
    if (!hasOwnProperty(m, "kind")) return false;

    return m.kind === decodeBarcodeRequestMessageName;
}

export interface BarcodeResponseMessage extends MessagePacketCommon {
    kind: typeof barcodeResponseMessageName;
    result: DecodeResult | undefined;
}

export function isBarcodeResponseMessage(m: unknown): m is BarcodeResponseMessage {
    return hasOwnProperty(m, "kind") && m.kind === barcodeResponseMessageName;
}

export interface ChunkAngleResponse {
    angleCluster: AngleCluster;
    xOffset: number;
    yOffset: number;
}

export interface ChunkAngleResponseMesssage extends MessagePacketCommon {
    kind: typeof chunkAngleResponseMessageName;
    responses: ChunkAngleResponse[];
}

export function isChunkAngleResponseMessage(m: unknown): m is ChunkAngleResponseMesssage {
    return hasOwnProperty(m, "kind") && m.kind === chunkAngleResponseMessageName;
}

export interface MessagingAdapter {
    postMessage(msg: any, transferList: any[]): void;
    addMessageListener(handler: (ev: MessageEvent<any>) => void): void;
}

export interface PossibleBarcodeImageResult {
    possibleBarcodes: PossibleBarcode[];
    barcodesImageData: ImageData[];
}

export interface ProcessFrameResult extends PossibleBarcodeImageResult {
    imageData: ImageData;
}
